import { useCallback, useMemo } from 'react';
import { SingularStationArea } from '@thive/fddm-types';

import { SelectInput } from '@common/components/inputs';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { areasToFlatCollection, isSingularStationArea } from '@/modules/floorplanDesign/helpers';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

const OPTIONS: { label: string; value: SingularStationArea['connectNeighbors'] }[] = [
  { label: 'No', value: 'NO_NEIGHBOR_MAPPING' },
  { label: 'Curved (forward only)', value: 'MAP_NEIGHBORS_WITHOUT_PIVOTS_FORWARD_ONLY' },
  { label: 'Curved (back and forth)', value: 'MAP_NEIGHBORS_WITHOUT_PIVOTS_BACK_AND_FORTH' },
  { label: 'Pivot (forward only)', value: 'MAP_NEIGHBORS_WITH_PIVOTS_FORWARD_ONLY' },
  { label: 'Pivot (back and forth)', value: 'MAP_NEIGHBORS_WITH_PIVOTS_BACK_AND_FORTH' },
];

export function SingularConnectNeighbors() {
  const selectedAreas = useReadSelectedAreas();
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const connectNeighbors = useMemo(() => {
    const areas = areasToFlatCollection(selectedAreas);
    if (areas.length !== 1) return;
    const area = areas[0];
    if (!isSingularStationArea(area)) return;
    return area.connectNeighbors;
  }, [selectedAreas]);

  const onChange = useCallback(
    (newValue: SingularStationArea['connectNeighbors']) => {
      const areas = areasToFlatCollection(selectedAreas);
      if (areas.length !== 1) return;
      const area = areas[0];
      if (!isSingularStationArea(area)) return;
      fddmSvc.updateArea({ ...area, connectNeighbors: newValue });
      fddmSvc.setPreference('singularStationArea.connectNeighbors', newValue);
    },
    [selectedAreas, fddmSvc],
  );

  if (connectNeighbors === undefined) return null;

  return <SelectInput options={OPTIONS} onChange={onChange} value={connectNeighbors} />;
}
